import { email, required, helpers } from 'vuelidate/lib/validators'

export default {

  results: {},

  /**
   * @returns {{dirty: boolean, hasErrors: boolean, blocks: {}}}
   */
  newBlockSet() {
    return {
      dirty: false,
      hasErrors: false,
      blocks: {}
    }
  },

  /**
   * @returns {{hasValidations: boolean, dirty: boolean, hasErrors: boolean, errors: Array}}
   */
  newResultSet() {
    return {
      hasValidations: false,
      dirty: false,
      hasErrors: false,
      errors: []
    }
  },

  initBlock(blockName, blockIndex, group, field, resultSet = null) {
    if (!this.results.hasOwnProperty(blockName)) {
      this.results[blockName] = this.newBlockSet()
    }

    if (!this.results[blockName]['blocks'].hasOwnProperty(blockIndex)) {
      this.results[blockName]['blocks'][blockIndex] = {}
    }

    let fieldKey = this.getKey(group, field)
    if (!this.results[blockName]['blocks'][blockIndex].hasOwnProperty(fieldKey)) {
      this.results[blockName]['blocks'][blockIndex][fieldKey] = this.newResultSet()
    }

    if (resultSet !== null) {
      this.results[blockName]['blocks'][blockIndex][this.getKey(group, field)] = resultSet
    }
  },

  getResult(blockName, blockIndex, group, field) {
    this.initBlock(blockName, blockIndex, group, field)
    return this.results[blockName]['blocks'][blockIndex][this.getKey(group, field)]
  },

  reset() {
    this.results = {}
  },

  /**
   * Validate the data for an entire block. This validates a multiple of a block! It expects the a data structure
   * where the first level is the block id, and the second level are the fields with there values. e.g.:
   *
   * block_0: {
   *   field_1: value_1,
   *   field_2: value_2
   * },
   * block_1: {
   *   field_1: value_A,
   *   field_2: value_B
   * }
   *
   * It does not return anything. The validation result is stored internal in the results property.
   *
   * @param {string} blockName The block name
   * @param {Object} data The block data
   * @param {Object} config Validation config
   */
  validateBlock(blockName, data, config) {
    if (typeof data === 'undefined') return

    if (this.results.hasOwnProperty(blockName)) {
      this.results[blockName].hasErrors = false
    }

    for (let blockIndex in data) {
      if (!data.hasOwnProperty(blockIndex)) continue
      const blockData = data[blockIndex]

      config.groups.forEach(vGroupConfig => {
        let fieldValidationResult = null

        vGroupConfig.fields.forEach(vFieldConfig => {
          if (vFieldConfig.hasOwnProperty('validators')) {
            let value = null

            if (blockData.hasOwnProperty(vGroupConfig.name) &&
              blockData[vGroupConfig.name].hasOwnProperty(vFieldConfig.id)) {
              value = blockData[vGroupConfig.name][vFieldConfig.id]
            }

            let resultSet = this.getResult(blockName, blockIndex, vGroupConfig.name, vFieldConfig.id)
            fieldValidationResult = this.validateField(resultSet, value, vFieldConfig.validators, vFieldConfig.type)
          } else {
            fieldValidationResult = this.newResultSet()
          }

          this.initBlock(blockName, blockIndex, vGroupConfig.name, vFieldConfig.id, fieldValidationResult)
          if (fieldValidationResult.hasErrors === true) {
            this.results[blockName].hasErrors = true
          }
        })
      })
    }
  },

  /**
   * Takes a single value and a list of validations and returns an object with "hasErrors" and "errors" properties.
   *  hasErrors: Whether or not ANY validator failed
   *  errors: Contains all validator types, that failed
   *
   * @param resultSet
   * @param value
   * @param validations
   * @param fieldType
   * @returns {{hasErrors: boolean, errors: Array}}
   */
  validateField(resultSet, value, validations, fieldType) {
    resultSet.hasValidations = true
    resultSet.hasErrors = false
    resultSet.errors = []

    validations.forEach(validation => {
      let fResult = null

      switch (validation.type) {
        case 'required':
          if (value == null) {
            fResult = false
          } else {
            if (fieldType === 'checkbox') {
              fResult = value !== false
            } else {
              fResult = required(value)
            }
          }
          break
        case 'email':
          fResult = email(value)
          break
        case 'min_length':
          fResult = value && value.length >= validation.value
          break
        case 'max_length':
          fResult = value && value.length <= validation.value
          break
        case 'starts_with':
          fResult = value && value.startsWith(validation.value)
          break
        case 'ends_with':
          fResult = value && value.endsWith(validation.value)
          break
        case 'telephone':
          const number = helpers.regex('serial', /\+[0-9]+$/)
          fResult = number(value)
          break
        default:
          fResult = true
      }

      if (fResult === false) {
        resultSet.hasErrors = true
        resultSet.errors.push(validation.type)
      }
    })

    return resultSet
  },

  getKey(group, field) {
    return group + '_' + field
  },

  touch(blockName, blockIndex, group, field) {
    if (!this.results.hasOwnProperty(blockName)) { return }
    if (!this.results[blockName].hasOwnProperty('blocks')) { return }

    this.results[blockName]['blocks'][blockIndex][this.getKey(group, field)].dirty = true
    this.results[blockName].dirty = true
  },

  isDirty(blockName, blockIndex, group, field) {
    this.initBlock(blockName, blockIndex, group, field)
    return this.results[blockName]['blocks'][blockIndex][this.getKey(group, field)].dirty
  },

  blockHasErrors(blockName) {
    if (!this.results.hasOwnProperty(blockName)) { return false }
    return this.results[blockName].hasErrors
  },

  hasError(blockName, blockIndex, group, field, includeClean = false) {
    let resultSet = this.getResult(blockName, blockIndex, group, field)
    if (includeClean) {
      return (resultSet.hasValidations && resultSet.hasErrors)
    } else {
      return (resultSet.hasValidations && resultSet.dirty && resultSet.hasErrors)
    }
  },

  hasAnyErrors() {
    let result = false

    for (let blockResultId in this.results) {
      if (
        this.results.hasOwnProperty(blockResultId) &&
        this.results[blockResultId].hasErrors === true
      ) {
        result = true
        break
      }
    }

    return result
  },

  isValid(group, field, includeClean = false) {
    if (this.isDirty(group, field)) {
      return !this.hasError(group, field, includeClean)
    }

    return false
  }

}
